import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { trpc } from '@/lib/trpc'

export interface CustomerOption {
  id: string
  name: string
  customerNumber?: string | null
}

/**
 * Kundenfeld mit Live-Suche statt langer Dropdown-Liste — tippen filtert
 * sofort nach Name/Kundennummer, Klick übernimmt. Ersetzt das reine
 * `<select>` auf Angebot/Rechnung, das bei vielen Kunden unübersichtlich wird.
 */
export function CustomerCombobox({
  value,
  onChange,
  label = 'Kunde',
}: {
  value: string
  onChange: (id: string, customer?: CustomerOption) => void
  label?: string
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  const list = trpc.customer.list.useQuery({ activeOnly: true, search: query.trim() || undefined })

  const selectedCustomer = useMemo(
    () => list.data?.find((c) => c.id === value),
    [list.data, value],
  )

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const rows = list.data ?? []

  return (
    <div className="w-full relative" ref={boxRef}>
      {label && <label className="label">{label}</label>}
      {value && selectedCustomer && !open ? (
        <button
          type="button"
          onClick={() => {
            setQuery('')
            setOpen(true)
          }}
          className="w-full flex items-center justify-between bg-bg-input border border-border rounded px-3 py-2 text-sm text-text-primary text-left hover:border-border-accent"
        >
          <span className="truncate">{selectedCustomer.name}</span>
          <X
            className="w-3.5 h-3.5 text-text-tertiary hover:text-danger flex-shrink-0 ml-2"
            onClick={(e) => {
              e.stopPropagation()
              onChange('')
            }}
          />
        </button>
      ) : (
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            className="w-full bg-bg-input border border-border rounded pl-8 pr-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-accent focus:ring-1 focus:ring-accent/30"
            placeholder="Kunde suchen (Name, Nummer) …"
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
          />
        </div>
      )}
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-bg-card border border-border rounded shadow-lg">
          {!rows.length ? (
            <p className="text-sm text-text-tertiary p-3">
              {list.isLoading ? 'Lädt …' : 'Keine Treffer.'}
            </p>
          ) : (
            rows.map((c) => (
              <button
                key={c.id}
                type="button"
                className={cn(
                  'w-full text-left px-3 py-2 text-sm hover:bg-bg-hover flex items-center justify-between',
                  c.id === value && 'bg-accent/10 text-accent',
                )}
                onClick={() => {
                  onChange(c.id, c)
                  setQuery('')
                  setOpen(false)
                }}
              >
                <span className="truncate text-text-primary">{c.name}</span>
                {c.customerNumber && (
                  <span className="text-2xs text-text-tertiary flex-shrink-0 ml-2">{c.customerNumber}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
